import { useState } from "react";
import { Button } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { toast } from "react-toastify";
import { ModalBox } from "../DashboardComponent/Outer/Modal";
import { AuctionPlaceBid } from "../redux/auth-reducer/auth-service";
import { readApiMessage } from "../utils/helper/helper";

const HEADING_STYLE = { fontSize: "14px", color: "#000", fontWeight: "bold" };
const FIGURE_STYLE = { fontSize: "16px", color: "#000" };
const COLUMN_STYLE = { padding: "10px", border: "1px solid #ccc", textAlign: "center" };

/**
 * Opens the Auction Winnings dialog of an auction, the way the player's grid and the manager's
 * Auctions list both do (FE-16): the dialog opens once Auction/ShowPlaceBid has answered.
 *
 * @returns {{open: (auctionId: number) => Promise<void>, modalProps: object}} `open` loads and
 *   shows an auction's winnings; spread `modalProps` on AuctionWinningsModal.
 */
export const useAuctionWinnings = () => {
  const [winnings, setWinnings] = useState(null);
  const [show, setShow] = useState(false);

  const open = async (auctionId) => {
    const result = await AuctionPlaceBid(auctionId);
    // No reply, or a 5xx: apiClient.js has already said so - this is a GET.
    if (!result || result.status >= 500) return;
    // Any other failure - a permission 401 has no body at all. It used to open the dialog on
    // that body, and the rows' .map threw and blanked the page.
    if (result.status >= 400) {
      toast.error(readApiMessage(result));
      return;
    }
    setWinnings(Array.isArray(result.data) ? result.data : []);
    setShow(true);
  };

  return { open, modalProps: { show, winnings, onClose: () => setShow(false) } };
};

const Figure = ({ label, children }) => (
  <div className="col-4 text-center">
    <div className="mb-2" style={HEADING_STYLE}>
      {label}
    </div>
    <div style={FIGURE_STYLE}>{children}</div>
  </div>
);

/**
 * An auction's pool and what each winning place takes: the rows of Auction/ShowPlaceBid, one per
 * place, each carrying the auction's pool figures.
 */
export const AuctionWinningsModal = ({ show, winnings, onClose }) => {
  const first = winnings?.[0];

  return (
    <ModalBox
      show={show}
      title={
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h1 className="heading-dash text-left">Auction Winnings</h1>
            </div>

            <div className="col-md-8">
              <div className="row justify-content-between">
                <Figure label="Total Pool">{(first?.totalPool || 0).toFixed(2)}</Figure>
                {/* It read "undefined%" until the rows had arrived. */}
                <Figure label="Manager Hold">
                  {first?.xcuttaTake != null ? `${first.xcuttaTake}%` : "-"}
                </Figure>
                <Figure label="Net Pool">{(first?.netPool || 0).toFixed(2)}</Figure>
              </div>
            </div>
          </div>
        </div>
      }
      body={
        <div className="row">
          <Table bordered>
            <thead>
              <tr>
                {["Winner Title", "Winner Percentage", "Winner Amount"].map((header) => (
                  <th key={header} className="bg-gray-view" style={COLUMN_STYLE}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {Array.isArray(winnings) && winnings.map((item, index) => (
                <tr key={index} style={{ textAlign: "center" }}>
                  <td>{item?.winnerTitle}</td>
                  <td>{`${item?.winnerPercentage}%`}</td>
                  <td>{(item?.winningAmount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    />
  );
};
